/* eslint-disable new-cap */
import { ControllerNodeBase, ControllerNodeOptions, Publish } from '@rta/rta-blueprint-library';

import { ComponentController } from './component-controller.class';
import { CursorMode } from './enums';
import { DesignerOptionsDataIn } from './types';
import { designerOptionsDefaults } from './defaults/designer-options-defaults';
import { DimensionOrientationMode, ShowDimensions } from '@r-core/types';
import { RenderLayerConfig } from '@r-services/render-layers';

export class DesignerOptions extends ControllerNodeBase<DesignerOptionsDataIn> {
	private _zoomIntervalTimer: ReturnType<typeof setTimeout> | null;
	private _controller: ComponentController;

	// performance tracking
	public fps: Record<string, { fps: number; count: number; avg: number; }>;
	public cursorMode: CursorMode;
	public contextMenu: boolean;
	public useFractions: boolean;
	public backgroundColor: string;
	public borderColor: string;
	public cutoutColor: string;
	public highlightFrontPanel: boolean;
	public isBlueprintView: boolean;

	#applianceOpacity: number;
	#cutoutOpacity: number;
	#readonly: boolean;

	#showAllDimensions: boolean;
	#showAppliances: boolean;
	#showApplianceDimensions: boolean;
	#showBreakpoints: boolean;
	#showCutouts: boolean;
	#showCountertops: boolean;
	#showCorners: boolean;
	#showDeveloperInsights: boolean;
	#showDimensions: boolean;
	#showDimensionsMode: ShowDimensions;
	#showHints: boolean;
	#showIslandBaseDimensions: boolean;
	#showTitle: boolean;
	#showState: boolean;
	#showOverlap: boolean;
	#showObstructions: boolean;
	#showBacksplashes: boolean;
	#showOverheadDimensions: boolean;
	#showPanelInsideDimensions: boolean;
	#showPanelOutsideDimensions: boolean;
	#showSkuLayer: boolean;
	#dimensionFontSizeFactor: number;
	#dimensionOrientationMode: DimensionOrientationMode;

	public showDisplayMode: boolean;
	public showZoom: boolean;

	public assetsBaseUrl: string | undefined;
	public assetsSasToken: string | undefined;
	public panelBottomPadding: number;

	public corner: {
		show: boolean;
		width: number;
		thickness: number;
	};

	// public coordinateSystem: {
	// 	canvas: {
	// 		width: number;
	// 		height: number;
	// 	};
	// 	viewport: {
	// 		xOffset: number;
	// 		yOffset: number;
	// 	};
	// 	margin: {
	// 		top: number;
	// 		bottom: number;
	// 		left: number;
	// 		right: number;
	// 	};
	// };

	readonly appliance: {
		fontSizeFactor: number;
		showLabel: boolean;
	};

	public dimensions: {
		fontSizeFactor: number;
	};

	// Customer
	public customerName: string;

	// Blueprint view metadata
	#designerName: string;
	#designerInitials: string;
	#currentUserInitials: string;
	#layoutDescription: string;
	#creationDate: string;
	#threeDImageUrl: string;

	// Appliance Groups
	public showApplianceGroups: boolean;

	// Render Layer Configuration
	// public renderLayerConfig: RenderLayerConfig;

	// Viewport Configuration - centralized viewport settings
	// public viewport: {
	// 	// Zoom settings
	// 	zoomFactor: number | null;
	// 	autoZoom: boolean | null;
	// 	autoZoomOverhead: boolean;

	// 	// Pan/Offset settings
	// 	xOffset: number;
	// 	yOffset: number;

	// 	// Scale settings
	// 	xScale: number;
	// 	yScale: number;
	// 	xScaleFactor: number;
	// 	yScaleFactor: number;

	// 	// Rotation settings
	// 	rotation: number;

	// 	// Animation settings
	// 	smoothZoomEnabled: boolean;
	// 	smoothZoomDuration: number;
	// 	smoothZoomEasing: 'linear' | 'ease-in' | 'ease-out' | 'ease-in-out';

	// 	// Viewport behavior
	// 	enableWheelZoom: boolean;
	// 	wheelZoomSensitivity: number;
	// 	enablePan: boolean;
	// 	panSensitivity: number;

	// 	// Constraints
	// 	minZoom: number;
	// 	maxZoom: number;
	// 	zoomSnap: number;
	// };

	constructor (options: ControllerNodeOptions<DesignerOptionsDataIn>, controller: ComponentController) {
		super('designer-options', options);
		const initOptions = {
			...designerOptionsDefaults,
			...options
		};

		this._controller = controller;

		// all options should be public
		Object.keys(initOptions)
			.forEach((key) => {
				this[key] = initOptions[key];
			});

		this.setInitialized();
	}

	@Publish('designer_options_readonly', 'updated')
	set readonly (readonly: boolean) { this.#readonly = readonly; }
	get readonly (): boolean { return this.#readonly; }

	@Publish('layer-option-appliance-opacity', 'updated')
	set applianceOpacity (opacity: number) { this.#applianceOpacity = opacity; }
	get applianceOpacity (): number { return this.#applianceOpacity; }

	@Publish('layer-option-cutout-opacity', 'updated')
	set cutoutOpacity (opacity: number) { this.#cutoutOpacity = opacity; }
	get cutoutOpacity (): number { return this.#cutoutOpacity; }

	@Publish('designer-options-adjust-font-size', 'updated')
	set dimensionFontSizeFactor (factor: number) { if (Number.isFinite(factor)) this.#dimensionFontSizeFactor = factor; }
	get dimensionFontSizeFactor (): number { return this.#dimensionFontSizeFactor }

	@Publish('designer-options-show-all-dimensions', 'updated')
	set showAllDimensions (show: boolean) { this.#showAllDimensions = show; }
	get showAllDimensions (): boolean { return this.#showAllDimensions; }

	@Publish('layer-option-appliance', 'updated')
	set showAppliances (show: boolean) { this.#showAppliances = show; }
	get showAppliances (): boolean { return this.#showAppliances; }

	@Publish('layer-option-appliance-dimensions', 'updated')
	set showApplianceDimensions (show: boolean) { this.#showApplianceDimensions = show; }
	get showApplianceDimensions (): boolean { return this.#showApplianceDimensions; }

	@Publish('layer-option-appliance-label', 'updated')
	set showApplianceLabels (show: boolean) { this.appliance.showLabel = show; }
	get showApplianceLabels (): boolean { return this.appliance.showLabel; }

	@Publish('layer-option-breakpoint', 'updated')
	set showBreakpoints (show: boolean) { this.#showBreakpoints = show; }
	get showBreakpoints (): boolean { return this.#showBreakpoints; }

	@Publish('layer-option-cutout', 'updated')
	set showCutouts (show: boolean) { this.#showCutouts = show; }
	get showCutouts (): boolean { return this.#showCutouts; }

	@Publish('layer-option-countertop', 'updated')
	set showCountertops (show: boolean) { this.#showCountertops = show; }
	get showCountertops (): boolean { return this.#showCountertops; }

	@Publish('layer-option-corner', 'updated')
	set showCorners (show: boolean) { this.#showCorners = show; }
	get showCorners (): boolean { return this.#showCorners; }

	@Publish('layer-option-developer-insight', 'updated')
	set showDeveloperInsights (show: boolean) { this.#showDeveloperInsights = show; }
	get showDeveloperInsights (): boolean { return this.#showDeveloperInsights; }

	@Publish('layer-option-dimensions', 'updated')
	set showDimensions (show: boolean) { this.#showDimensions = show; }
	get showDimensions (): boolean { return this.#showDimensions; }

	@Publish('layer-option-hint', 'updated')
	set showHints (show: boolean) { this.#showHints = show; }
	get showHints (): boolean { return this.#showHints; }

	@Publish('layer-option-island-base-dimension', 'updated')
	set showIslandBaseDimensions (show: boolean) { this.#showIslandBaseDimensions = show; }
	get showIslandBaseDimensions (): boolean { return this.#showIslandBaseDimensions; }

	@Publish('layer-option-title', 'updated')
	set showTitle (show: boolean) { this.#showTitle = show; }
	get showTitle (): boolean { return this.#showTitle; }

	@Publish('layer-option-state', 'updated')
	set showState (show: boolean) { this.#showState = show; }
	get showState (): boolean { return this.#showState; }

	@Publish('layer-option-overlap', 'updated')
	set showOverlap (show: boolean) { this.#showOverlap = show; }
	get showOverlap (): boolean { return this.#showOverlap; }

	@Publish('layer-option-obstruction', 'updated')
	set showObstructions (show: boolean) { this.#showObstructions = show; }
	get showObstructions (): boolean { return this.#showObstructions; }

	@Publish('layer-option-backsplash', 'updated')
	set showBacksplashes (show: boolean) { this.#showBacksplashes = show; }
	get showBacksplashes (): boolean { return this.#showBacksplashes; }

	@Publish('layer-option-overhead-dimension', 'updated')
	set showOverheadDimensions (show: boolean) { this.#showOverheadDimensions = show; }
	get showOverheadDimensions (): boolean { return this.#showOverheadDimensions; }

	@Publish('layer-option-panel-inside-dimension', 'updated')
	set showPanelInsideDimensions (show: boolean) { this.#showPanelInsideDimensions = show; }
	get showPanelInsideDimensions (): boolean { return this.#showPanelInsideDimensions; }

	@Publish('layer-option-panel-outside-dimension', 'updated')
	set showPanelOutsideDimensions (show: boolean) { this.#showPanelOutsideDimensions = show; }
	get showPanelOutsideDimensions (): boolean { return this.#showPanelOutsideDimensions; }

	@Publish('layer-option-sku-layer', 'updated')
	set showSkuLayer (show: boolean) { this.#showSkuLayer = show; }
	get showSkuLayer (): boolean { return this.#showSkuLayer; }

	@Publish('component-option-dimensions-mode', 'updated')
	set showDimensionsMode (show: ShowDimensions) { this.#showDimensionsMode = show; }
	get showDimensionsMode (): ShowDimensions { return this.#showDimensionsMode; }

	@Publish('component-option-dimension-orientation-mode', 'updated')
	set dimensionOrientationMode (mode: DimensionOrientationMode) { this.#dimensionOrientationMode = mode; }
	get dimensionOrientationMode (): DimensionOrientationMode { return this.#dimensionOrientationMode; }

	@Publish('blueprint-view-designer-name', 'updated')
	set designerName (name: string) { this.#designerName = name; }
	get designerName (): string { return this.#designerName; }

	@Publish('blueprint-view-designer-initials', 'updated')
	set designerInitials (initials: string) { this.#designerInitials = initials; }
	get designerInitials (): string { return this.#designerInitials; }

	@Publish('blueprint-view-current-user-initials', 'updated')
	set currentUserInitials (initials: string) { this.#currentUserInitials = initials; }
	get currentUserInitials (): string { return this.#currentUserInitials; }

	@Publish('blueprint-view-layout-description', 'updated')
	set layoutDescription (description: string) { this.#layoutDescription = description; }
	get layoutDescription (): string { return this.#layoutDescription; }

	@Publish('blueprint-view-creation-date', 'updated')
	set creationDate (date: string) { this.#creationDate = date; }
	get creationDate (): string { return this.#creationDate; }

	@Publish('blueprint-view-three-d-image-url', 'updated')
	set threeDImageUrl (url: string) { this.#threeDImageUrl = url; }
	get threeDImageUrl (): string { return this.#threeDImageUrl; }
}

